import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import enTranslations from '../translations/en.json';
import frTranslations from '../translations/fr.json';
import arTranslations from '../translations/ar.json';
import kuTranslations from '../translations/ku.json';

// Available languages
export const languages = {
  en: 'English',
  fr: 'Français',
  ar: 'العربية',
  ku: 'کوردی'
};

// Translations object for all supported languages
const translations = {
  en: enTranslations,
  fr: frTranslations,
  ar: arTranslations,
  ku: kuTranslations
};

// Get browser language or fallback to English
const getBrowserLanguage = () => {
  const browserLang = navigator.language.split('-')[0];
  return Object.keys(languages).includes(browserLang) ? browserLang : 'en';
};

// Create a Zustand store for language settings
export const useTranslationStore = create(
  persist(
    (set, get) => ({
      language: getBrowserLanguage(),
      setLanguage: (language) => {
        set({ language });
        updateDocumentDirection(language);
      },
      t: (key, replacements, fallback) => translate(key, get().language, replacements, fallback),
      getDirection: () => getLanguageDirection(get().language),
      getTranslations: () => translations[get().language] || translations.en
    }),
    {
      name: 'language-storage', // unique name for localStorage
    }
  )
);

// Helper function to update document direction based on language
const updateDocumentDirection = (language) => {
  const direction = getLanguageDirection(language);
  document.documentElement.dir = direction;
  document.documentElement.lang = language;
  
  // Add or remove RTL class from body for additional styling
  if (direction === 'rtl') {
    document.body.classList.add('rtl');
  } else {
    document.body.classList.remove('rtl');
  }
};

// Get language direction (LTR or RTL)
export const getLanguageDirection = (language) => {
  return translations[language]?.directionality || 'ltr';
};

// Initialize document direction on page load
if (typeof window !== 'undefined') {
  const language = useTranslationStore.getState().language;
  updateDocumentDirection(language);
}

// Function to get translation for a given key
export const translate = (key, language, replacements = {}, fallback = null) => {
  if (!key) return '';
  
  const keys = key.split('.');
  let result = translations[language] || translations.en;
  
  // Navigate through the nested keys
  for (const k of keys) {
    if (result[k] === undefined) {
      // If translation is missing, fallback to English
      result = translations.en;
      for (const fallbackKey of keys) {
        if (result[fallbackKey] === undefined) {
          console.warn(`Translation key not found: ${key}`);
          // Use fallback if provided, otherwise return the key itself
          return fallback || key;
        }
        result = result[fallbackKey];
      }
      return result;
    }
    result = result[k];
  }
  
  // Replace template variables if replacements are provided
  if (typeof result === 'string' && Object.keys(replacements).length > 0) {
    // Replace {{key}} with the corresponding value from replacements
    return result.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return replacements[key] !== undefined ? replacements[key] : match;
    });
  }
  
  return result;
};

// React hook for using translations in components
export const useTranslation = () => {
  const { language, setLanguage, t, getDirection, getTranslations } = useTranslationStore();
  
  return {
    t, // Translate function
    language, // Current language
    setLanguage, // Function to change language
    direction: getDirection(), // Current text direction (ltr/rtl)
    languages, // Available languages
    translations: getTranslations() // All translations for current language
  };
};

export default useTranslation; 